import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import ScopeField from '../../components/ScopeField';
import AccumulatedSummaryResultsView from './AccumulatedSummaryResultsView';
import { fetchAccumulatedSummary } from '../../api/accumulatedSummary';
import { todayISO } from '../../utils/date';
import './AccumulatedSummaryPage.css';

const isoToDisplay = (iso) => {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

const documentTypeOptions = ['Online', 'Local', 'Local On Time', 'Local Overtime'];
const orderByOptions = ['Code', 'Name'];
const displayModeOptions = ['Global', 'Bank', 'Supervisor', 'Group', 'Lottery', 'Date'];

const AccumulatedSummaryPage = () => {
  const [filter, setFilter] = useState('All');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [lotteryFrom, setLotteryFrom] = useState('0');
  const [lotteryFromName, setLotteryFromName] = useState('');
  const [lotteryTo, setLotteryTo] = useState('0');
  const [lotteryToName, setLotteryToName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [orderBy, setOrderBy] = useState('Code');
  const [displayMode, setDisplayMode] = useState('Global');
  const [showResults, setShowResults] = useState(false);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (filter !== 'All' && !scopeId) {
      setError(`Choisis d'abord le ${filter.toLowerCase()} (N°, nom ou 🔍).`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchAccumulatedSummary({
        filter,
        scope: filter !== 'All' ? scopeId : undefined,
        dateFrom,
        dateTo,
        lotteryFrom: lotteryFrom !== '0' ? lotteryFrom : undefined,
        lotteryUp: lotteryTo !== '0' ? lotteryTo : undefined,
        documentType,
        displayMode,
        orderBy,
      });
      setReport(data);
      setShowResults(true);
    } catch (err) {
      setError('Recherche impossible.');
    } finally {
      setLoading(false);
    }
  };

  if (showResults && report) {
    return (
      <AccumulatedSummaryResultsView
        report={report}
        filter={filter}
        displayMode={displayMode}
        dateFrom={isoToDisplay(dateFrom)}
        dateTo={isoToDisplay(dateTo)}
        generatedAt={new Date().toISOString().slice(0, 19).replace('T', ' ')}
        onBack={() => setShowResults(false)}
      />
    );
  }

  return (
    <Layout>
      <p className="as-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Reports</Link>
        {' >> '}
        <span className="bc-current">Accumulated Summary</span>
      </p>

      <div className="as-card">
        <div className="as-card-header">
          <span className="as-card-title">Summarized Accumulated</span>
        </div>
        <div className="as-card-body">
          <div className="as-radio-row">
            {['All', 'Group', 'Supervisor', 'Bank'].map((opt) => (
              <label key={opt} className="as-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'All' && (
            <div className="as-row as-scope-row">
              <label className="as-label">{filter}</label>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="as-input as-input-sm"
                iconClass="as-icon-btn"
                nameClass="as-lottery-name"
              />
            </div>
          )}

          <form onSubmit={handleSearch} className="as-form">
            <div className="as-row">
              <label className="as-label">From</label>
              <DateField className="as-input as-input-date" value={dateFrom} onChange={setDateFrom} />

              <label className="as-label as-label-to">To</label>
              <DateField className="as-input as-input-date" value={dateTo} onChange={setDateTo} />
            </div>

            <div className="as-row">
              <label className="as-label">Lottery From</label>
              <LotteryCodeField
                value={lotteryFrom}
                name={lotteryFromName}
                onChange={(code) => { setLotteryFrom(code); setLotteryFromName(''); }}
                onResolve={(code, nm) => { setLotteryFrom(code); setLotteryFromName(nm); }}
                inputClass="as-input as-input-sm"
                iconClass="as-icon-btn"
                nameClass="as-lottery-name"
              />

              <label className="as-label as-label-to">Lottery To</label>
              <LotteryCodeField
                value={lotteryTo}
                name={lotteryToName}
                onChange={(code) => { setLotteryTo(code); setLotteryToName(''); }}
                onResolve={(code, nm) => { setLotteryTo(code); setLotteryToName(nm); }}
                inputClass="as-input as-input-sm"
                iconClass="as-icon-btn"
                nameClass="as-lottery-name"
              />
            </div>

            <div className="as-row">
              <label className="as-label">Document Type</label>
              <select className="as-select" value={documentType} onChange={e => setDocumentType(e.target.value)}>
                <option value=""></option>
                {documentTypeOptions.map(opt => <option key={opt}>{opt}</option>)}
              </select>

              <label className="as-label as-label-to">Order by</label>
              <select className="as-select" value={orderBy} onChange={e => setOrderBy(e.target.value)}>
                {orderByOptions.map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="as-row">
              <label className="as-label">Display Mode</label>
              <select className="as-select" value={displayMode} onChange={e => setDisplayMode(e.target.value)}>
                {displayModeOptions.map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>

            {error && <p className="as-error">{error}</p>}

            <button type="submit" className="as-search-btn" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AccumulatedSummaryPage;
